import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SEOHead } from "@/components/SEOHead";
import { ProductCard } from "@/components/ProductCard";

const FILTERS = [
  { key: "all", label: "Everything" },
  { key: "cakes", label: "Cakes / Pastry" },
  { key: "tea-cakes", label: "Tea Time Cakes" },
  { key: "artisan-savouries", label: "Artisan Savouries" },
  { key: "cookies", label: "Cookies" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "all";
  const [active, setActive] = useState(initial);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setActive(params.get("category") || "all");
  }, [params]);

  useEffect(() => {
    setLoading(true);
    const url = active === "all" ? "/products" : `/products?category=${active}`;
    api.get(url)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [active]);

  const setCategory = (key) => {
    if (key === "all") setParams({});
    else setParams({ category: key });
  };

  const activeFilter = FILTERS.find((f) => f.key === active) || FILTERS[0];
  const catLabel = activeFilter.key === "all" ? "Cakes, Pastries, Breads & Cookies" : activeFilter.label;
  const seoTitle = active === "all"
    ? "Shop Cakes, Pastries & Breads Online in Mumbai | Cocoamaya"
    : `Buy ${activeFilter.label} Online in Mumbai | Cocoamaya Patisserie`;
  const seoDesc = `Shop ${catLabel.toLowerCase()} from Cocoamaya Patisserie — hand-crafted in Mumbai with same-day delivery. Fresh from our bakery every morning.`;
  const keywordsByCat = {
    all: "cake shop mumbai, buy cake online mumbai, artisan savouries mumbai, cookies mumbai, patisserie mumbai, same day cake delivery mumbai",
    cakes: "cakes mumbai, pastry mumbai, birthday cake mumbai, chocolate cake mumbai, buy cake online mumbai, celebration cakes, gourmet cakes andheri, patisserie cakes",
    "tea-cakes": "tea time cakes mumbai, loaf cake, everyday cake, pound cake, banana cake, cocoamaya",
    "artisan-savouries": "artisan savouries mumbai, savoury croissants mumbai, focaccia mumbai, veg puff, chicken puff, egg puff, thecha paneer, butter chicken croissant, vada pav croissant",
    cookies: "cookies mumbai, macarons mumbai, french macarons, chocolate chip cookies, gourmet cookies mumbai",
  };
  const seoKeywords = keywordsByCat[active] || keywordsByCat.all;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="shop-page">
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        url={`https://cocoamayapatisserie.com/shop${active === "all" ? "" : `?category=${active}`}`}
      />
      <header className="mb-12 md:mb-16">
        <p className="eyebrow mb-3">The Bakery</p>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight">Shop everything fresh.</h1>
      </header>

      <div className="flex flex-wrap gap-2 md:gap-3 mb-12">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setCategory(f.key)}
            className={`eyebrow px-5 py-2.5 border transition-colors`}
            style={{
              background: active === f.key ? "var(--ink)" : "transparent",
              color: active === f.key ? "var(--bg)" : "var(--ink)",
              borderColor: "var(--ink)",
            }}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: "var(--ink-soft)" }}>Loading…</p>
      ) : products.length === 0 ? (
        <p style={{ color: "var(--ink-soft)" }}>No products in this category yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-10">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
